import React from "react";
import { ChevronDown } from "lucide-react";
import cn from "./cn";
import {
  DEFAULT_PHONE_COUNTRY,
  PINNED_PHONE_COUNTRIES,
  callingCodeOf,
  canonicalPhone,
  countryOfTyped,
  phoneCountries,
  phoneProblem,
  splitPhone,
} from "../../utils/phone";

/**
 * Input · Textarea · NativeSelect — the app's text-entry controls.
 *
 * One spec, three sizes:
 *
 *   sm  32px — dense toolbars, inline editing inside a table
 *   md  36px — the default; every toolbar in the app is built around it
 *   lg  44px — form and drawer fields, where a control sits under its own label
 *
 * They read the `--input-*` tokens, so a control from this file and a
 * react-bootstrap `<Form.Control>` beside it are the same control in both themes
 * and under the print palette.
 *
 * ⚠️ `tw-rounded-md` is **10px** here, not Tailwind's 6px — `borderRadius` is
 * remapped in `tailwind.config.js`. Run `npm run verify:tw` to print the scale.
 */

// ⚠️ `tw-border-solid` is load-bearing, not decoration. Preflight is off, so
// nothing in this app declares a global border-style, and `tw-border` sets a
// WIDTH and a COLOUR onto whatever style the UA already had. On an <input>,
// <select> or <textarea> that is `inset` — so every control in the library has
// been drawing a bevelled field while the SCSS form pages beside them write the
// shorthand `border: 1px solid …` and draw a flat line. That difference is what
// made a filter drawer not look like a form. Same note as PanelButton, which is
// where it was first caught.
const CONTROL_BASE_CLASSES =
  "tw-rounded-md tw-border tw-border-solid tw-bg-input tw-text-input-fg tw-outline-none " +
  "tw-transition-[border-color,box-shadow] tw-duration-150 " +
  "placeholder:tw-text-content-subtle " +
  "disabled:tw-cursor-not-allowed disabled:tw-bg-surface-alt disabled:tw-text-content-subtle";

// Valid and invalid differ only in hue — same border width, same ring geometry.
// An errored field has to read as the same control in trouble, not as a
// different control.
const VALID_STATE_CLASSES =
  "tw-border-input-line focus:tw-border-primary/40 focus:tw-shadow-focus";
const INVALID_STATE_CLASSES =
  "tw-border-danger focus:tw-border-danger focus:tw-shadow-focus-danger";

const CONTROL_SIZE_CLASSES = {
  sm: "tw-h-8 tw-px-2.5 tw-text-[0.8125rem]",
  md: "tw-h-9 tw-px-3 tw-text-sm",
  lg: "tw-h-11 tw-px-3.5 tw-text-sm",
};

// A textarea grows, so its height comes from padding and `rows`. The vertical
// padding is chosen so a single-row textarea lines up with the fixed-height
// control above it in a stacked form.
const TEXTAREA_SIZE_CLASSES = {
  sm: "tw-px-2.5 tw-py-1.5 tw-text-[0.8125rem]",
  md: "tw-px-3 tw-py-2 tw-text-sm",
  lg: "tw-px-3.5 tw-py-2.5 tw-text-sm",
};

// How much room a `trailing` node needs on the right. A closed vocabulary rather
// than a free padding class, because the reserve has to resolve to exactly ONE
// utility: `cn()` does not merge, and Tailwind emits `tw-pr-16` BEFORE `tw-pr-9`,
// so a caller passing a wider padding through `className` would silently lose to
// the default. `icon` fits a glyph; `text` fits a short label — a character
// counter, a unit, a currency code.
const TRAILING_PAD_CLASSES = {
  icon: "tw-pr-9",
  text: "tw-pr-16",
};

const CONTROL_SIZES = new Set(["sm", "md", "lg"]);

const warnedSizes = new Set();

/** An unrecognised size would otherwise render a control with no padding at all,
 *  so it resolves to `md` and says so once in development. */
const resolveSize = (size) => {
  if (CONTROL_SIZES.has(size)) return size;
  if (process.env.NODE_ENV !== "production" && !warnedSizes.has(size)) {
    warnedSizes.add(size);
    // eslint-disable-next-line no-console
    console.warn(
      `[FormControls] unknown size "${size}" — falling back to "md". Valid sizes: sm, md, lg.`
    );
  }
  return "md";
};

const warnedTrailingPads = new Set();

/** Same treatment as `size`: an unknown reserve would leave the trailing node
 *  sitting on top of the value. */
const resolveTrailingPad = (pad) => {
  if (Object.hasOwn(TRAILING_PAD_CLASSES, pad)) return pad;
  if (process.env.NODE_ENV !== "production" && !warnedTrailingPads.has(pad)) {
    warnedTrailingPads.add(pad);
    // eslint-disable-next-line no-console
    console.warn(
      `[FormControls] unknown trailingPad "${pad}" — falling back to "icon". ` +
        `Valid values: ${Object.keys(TRAILING_PAD_CLASSES).join(", ")}.`
    );
  }
  return "icon";
};

const controlClass = ({ size, invalid, fullWidth, sizeClasses = CONTROL_SIZE_CLASSES }) =>
  cn(
    CONTROL_BASE_CLASSES,
    sizeClasses[resolveSize(size)],
    invalid ? INVALID_STATE_CLASSES : VALID_STATE_CLASSES,
    fullWidth && "tw-w-full"
  );

/** The error tone fires on either the `invalid` prop or an injected
 *  `aria-invalid`. `FormField` wires the accessibility attribute onto whatever
 *  child it is given and cannot know that child is one of these, so reading it
 *  back here is what keeps "announced as invalid" and "looks invalid" the same
 *  state. */
const isInvalid = (invalid, props) =>
  invalid || props["aria-invalid"] === true || props["aria-invalid"] === "true";

/**
 * Input — a text-entry control.
 *
 * Props beyond the native ones:
 *   size       — "sm" | "md" | "lg"  (default "md")
 *   invalid    — error tone, and sets aria-invalid
 *   fullWidth  — stretch to the container (default true)
 *   leading    — node rendered inside the left edge (an icon, a currency mark)
 *   trailing   — node rendered inside the right edge (a clear button, a unit)
 *   trailingPad— how much room `trailing` needs: "icon" (default) | "text"
 *
 * Decorations are `pointer-events-none` by default so they never steal a click
 * meant for the field; an interactive `trailing` node opts back in through its
 * own handlers.
 */
const Input = React.forwardRef(
  (
    {
      size = "md",
      invalid = false,
      fullWidth = true,
      leading,
      trailing,
      trailingPad = "icon",
      className = "",
      wrapperClassName = "",
      type = "text",
      ...props
    },
    ref
  ) => {
    const errored = isInvalid(invalid, props);
    const control = (
      <input
        ref={ref}
        type={type}
        aria-invalid={errored || undefined}
        className={cn(
          controlClass({ size, invalid: errored, fullWidth }),
          leading && "tw-pl-9",
          trailing && TRAILING_PAD_CLASSES[resolveTrailingPad(trailingPad)],
          className
        )}
        {...props}
      />
    );

    if (!leading && !trailing) return control;

    return (
      <span
        className={cn(
          "tw-relative tw-inline-flex tw-items-center",
          fullWidth && "tw-w-full",
          wrapperClassName
        )}
      >
        {leading && (
          <span className="tw-pointer-events-none tw-absolute tw-left-3 tw-inline-flex tw-items-center tw-text-content-subtle">
            {leading}
          </span>
        )}
        {control}
        {trailing && (
          <span className="tw-absolute tw-right-2.5 tw-inline-flex tw-items-center tw-text-content-subtle">
            {trailing}
          </span>
        )}
      </span>
    );
  }
);
Input.displayName = "Input";

/**
 * Textarea — the multi-line control, same tone and focus state as Input.
 * Anything taller than the default is a `rows` prop, not a height class.
 */
const Textarea = React.forwardRef(
  (
    { size = "md", invalid = false, fullWidth = true, rows = 3, className = "", ...props },
    ref
  ) => {
    const errored = isInvalid(invalid, props);
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={errored || undefined}
        className={cn(
          controlClass({
            size,
            invalid: errored,
            fullWidth,
            sizeClasses: TEXTAREA_SIZE_CLASSES,
          }),
          "tw-resize-y",
          className
        )}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

/**
 * NativeSelect — a `<select>` at the app's control geometry.
 *
 * Accepts either `options={[{ value, label, disabled? }]}` or ordinary
 * `<option>` children. `onChange` receives the native event, so this is a
 * drop-in for `<select>`; `SelectControl` is the value-first wrapper for
 * toolbars.
 */
const NativeSelect = React.forwardRef(
  (
    {
      size = "md",
      invalid = false,
      fullWidth = true,
      options,
      children,
      className = "",
      wrapperClassName = "",
      disabled = false,
      ...props
    },
    ref
  ) => {
    const errored = isInvalid(invalid, props);
    return (
      // The chevron is an overlaid glyph rather than a background image so it can
      // take a token colour. `appearance-none` removes the native one, and
      // without a replacement the control reads as a text input.
      <span
        className={cn(
          "tw-relative tw-inline-flex tw-items-center",
          fullWidth && "tw-w-full",
          wrapperClassName
        )}
      >
        <select
          ref={ref}
          disabled={disabled}
          aria-invalid={errored || undefined}
          className={cn(
            controlClass({ size, invalid: errored, fullWidth }),
            "tw-cursor-pointer tw-appearance-none tw-pr-8 disabled:tw-cursor-not-allowed",
            className
          )}
          {...props}
        >
          {options
            ? options.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))
            : children}
        </select>
        <svg
          className="tw-pointer-events-none tw-absolute tw-right-2.5 tw-text-content-muted"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    );
  }
);
NativeSelect.displayName = "NativeSelect";

// The country segment stands at the height of the control beside it; its
// padding is its own (a second `px` would be decided by CSS order, not by cn).
const SEGMENT_SIZE_CLASSES = {
  sm: "tw-h-8 tw-text-[0.8125rem]",
  md: "tw-h-9 tw-text-sm",
  lg: "tw-h-11 tw-text-sm",
};

/**
 * CountryCodeSelect — the "+91 ▾" segment in front of a phone number.
 *
 * A native `<select>` laid transparently over a short label: the open list
 * shows each country's name, the closed control only its dialling code, and
 * the keyboard, screen-reader and phone pickers are the platform's own. Type a
 * country's first letters to jump to it.
 *
 * Value-first: `value` is an ISO code ("IN"), `onChange(code)`.
 * Use `PhoneInput` for a phone field; this is exported for the sign-in box,
 * whose one field takes an email OR a number.
 */
const CountryCodeSelect = React.forwardRef(
  (
    {
      value = DEFAULT_PHONE_COUNTRY,
      onChange,
      size = "md",
      invalid = false,
      disabled = false,
      className = "",
      "aria-label": ariaLabel = "Country code",
      ...props
    },
    ref
  ) => {
    const countries = phoneCountries();
    const pinned = PINNED_PHONE_COUNTRIES.map((code) => countries.find((c) => c.code === code)).filter(Boolean);
    const selected = countries.find((c) => c.code === value);
    const option = (c, group) => (
      <option key={`${group}-${c.code}`} value={c.code}>
        {c.name} (+{c.callingCode})
      </option>
    );

    return (
      <span
        className={cn(
          "tw-relative tw-inline-flex tw-w-20 tw-shrink-0 tw-items-center tw-justify-between tw-gap-1 tw-px-2.5",
          "tw-rounded-md tw-border tw-border-solid tw-transition-[border-color,box-shadow] tw-duration-150",
          SEGMENT_SIZE_CLASSES[resolveSize(size)],
          invalid
            ? "tw-border-danger focus-within:tw-shadow-focus-danger"
            : "tw-border-input-line focus-within:tw-border-primary/40 focus-within:tw-shadow-focus",
          disabled
            ? "tw-cursor-not-allowed tw-bg-surface-alt tw-text-content-subtle"
            : "tw-bg-input tw-text-input-fg",
          className
        )}
      >
        <span aria-hidden="true" className="tw-tabular-nums">
          {callingCodeOf(value) || "+"}
        </span>
        <ChevronDown size={14} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />
        <select
          ref={ref}
          value={value}
          disabled={disabled}
          aria-label={ariaLabel}
          title={selected?.name}
          onChange={(e) => onChange?.(e.target.value)}
          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-cursor-pointer tw-opacity-0 disabled:tw-cursor-not-allowed"
          {...props}
        >
          <optgroup label="Common">{pinned.map((c) => option(c, "pinned"))}</optgroup>
          <optgroup label="All countries">{countries.map((c) => option(c, "all"))}</optgroup>
        </select>
      </span>
    );
  }
);
CountryCodeSelect.displayName = "CountryCodeSelect";

const sameCallingCode = (a, b) => callingCodeOf(a) === callingCodeOf(b);

/**
 * PhoneInput — a mobile number with its country: [+91 ▾] [98765 43210].
 *
 * Value-first, in the STORED shape (`utils/phone`): `value` is what the record
 * holds and `onChange(next)` hands back what it should hold — 10 digits for
 * India, "+E.164" for anywhere else. India is the default; a number typed or
 * pasted with its own "+" moves the picker to that country.
 *
 * It tells the browser when the number is unusable (`setCustomValidity`), so
 * a form relying on native validation still blocks submit the way its old
 * `pattern="[0-9]{10}"` did. A form that validates itself passes
 * `nativeValidation={false}` and asks `phoneProblem()` for the message.
 *
 * `id`, `aria-*`, `required`, `name` and the rest land on the number box, so
 * `FormField` wires it like any other control.
 */
const PhoneInput = React.forwardRef(
  (
    {
      value = "",
      onChange,
      defaultCountry = DEFAULT_PHONE_COUNTRY,
      size = "md",
      invalid = false,
      fullWidth = true,
      disabled = false,
      nativeValidation = true,
      placeholder,
      countryLabel = "Country code",
      className = "",
      wrapperClassName = "",
      ...props
    },
    ref
  ) => {
    const [state, setState] = React.useState(() => splitPhone(value, defaultCountry));
    // What this control last handed out — a `value` equal to it is our own
    // echo; anything else is the form loading or resetting the record.
    const emitted = React.useRef(value ?? "");
    const inputRef = React.useRef(null);

    React.useEffect(() => {
      const next = value ?? "";
      if (next === emitted.current) return;
      emitted.current = next;
      setState(splitPhone(next, defaultCountry));
    }, [value, defaultCountry]);

    const emit = (next) => {
      emitted.current = next;
      onChange?.(next);
    };

    const handleNumber = (e) => {
      const text = e.target.value.replace(/[^\d\s+().-]/g, "");
      const typed = countryOfTyped(text);
      if (typed) {
        // "+44 7911 123456" names its own country. Keep the picker's choice
        // when it dials the same code — a UK mobile may parse as Guernsey.
        const country = sameCallingCode(state.country, typed.country) ? state.country : typed.country;
        setState({ country, national: typed.national });
        emit(canonicalPhone(text, country));
        return;
      }
      setState((prev) => ({ ...prev, national: text }));
      emit(canonicalPhone(text, state.country));
    };

    // Focus stays on the picker: arrowing through a closed <select> fires a
    // change per keypress, and moving focus would end the browse at one step.
    const handleCountry = (country) => {
      setState((prev) => ({ ...prev, country }));
      emit(canonicalPhone(state.national, country));
    };

    const problem = phoneProblem(canonicalPhone(state.national, state.country));
    React.useEffect(() => {
      inputRef.current?.setCustomValidity(nativeValidation && problem ? problem : "");
    }, [nativeValidation, problem]);

    const setRefs = React.useCallback(
      (node) => {
        inputRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const errored = isInvalid(invalid, props);
    const isIndia = state.country === DEFAULT_PHONE_COUNTRY;

    return (
      <span className={cn("tw-flex tw-items-center tw-gap-2", fullWidth && "tw-w-full", wrapperClassName)}>
        <CountryCodeSelect
          value={state.country}
          onChange={handleCountry}
          size={size}
          invalid={errored}
          disabled={disabled || props.readOnly}
          aria-label={countryLabel}
        />
        <Input
          ref={setRefs}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          maxLength={24}
          size={size}
          invalid={invalid}
          disabled={disabled}
          // A form's placeholder describes an Indian number ("10-digit number").
          placeholder={isIndia ? placeholder : "Mobile number"}
          className={cn("tw-min-w-0 tw-flex-1", className)}
          {...props}
          value={state.national}
          onChange={handleNumber}
        />
      </span>
    );
  }
);
PhoneInput.displayName = "PhoneInput";

export { Input, Textarea, NativeSelect, CountryCodeSelect, PhoneInput };
